import { Fragment, useEffect, useState } from "react";
import {
  Bell,
  Smartphone,
  Mail,
  Power,
  Check,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  UploadCloud,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import notificationService, {
  ChannelToggle,
  NotificationPreferences,
} from "../../../services/notification.service";

/**
 * Preferences tab — backed by `GET / PUT /notification-srv/preferences`.
 *
 * Layout (top → bottom):
 *   1. Master switch — global enable for ALL DocxIQ notifications.
 *   2. Channels — In-app / Push / Email global toggles + quiet hours.
 *   3. Per-event matrix — Documents-module event types in Phase 1; new
 *      modules append rows here later without restructuring.
 *
 * Save flow: every change updates local state; the explicit Save button
 * sends the full preferences payload to the backend.
 */

interface EventDef {
  /** Server-side key — matches NotificationEventType enum values. */
  id: string;
  module: string;
  label: string;
  description: string;
  icon: JSX.Element;
  /** Channels admins force-on (user can't disable). */
  required?: (keyof ChannelToggle)[];
  /** Channels that are unavailable for this event (locked OFF in UI; backend won't send). */
  unsupported?: (keyof ChannelToggle)[];
}

const DOCUMENT_EVENTS: EventDef[] = [
  {
    id: "DOCUMENT_UPLOADED",
    module: "Documents",
    label: "Document uploaded",
    description:
      "Notifies you each time a file finishes uploading to one of your folders. Useful as a quick receipt that the upload reached the server before processing starts.",
    icon: <UploadCloud className="w-4 h-4 text-blue-500" />,
    // Email isn't sent for the upload event — only processing-completed
    // and processing-failed produce emails (the backend enforces this).
    unsupported: ["email"],
  },
  {
    id: "DOCUMENT_PROCESSED",
    module: "Documents",
    label: "Processing completed",
    description:
      "Sent when DocxIQ finishes AI processing on a file (vision + RAG). That's when the file becomes searchable, the summary is ready, and downstream features (Campaign Library, Document Search) can use it.",
    icon: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
  },
  {
    id: "DOCUMENT_PROCESSING_FAILED",
    module: "Documents",
    label: "Processing failed",
    description:
      "Sent when any processing stage errors out — vision, OCR or RAG. Needs your attention because the file won't appear in search or the campaign library until you re-process it. Bell is forced on so failures can't be silently missed.",
    icon: <XCircle className="w-4 h-4 text-red-500" />,
    required: ["bell"],
  },
];

// More modules append here later (Reports / Workflows / Collaboration / Security).
const EVENT_GROUPS: { module: string; events: EventDef[] }[] = [
  { module: "Documents", events: DOCUMENT_EVENTS },
];

const CHANNEL_META: Array<{
  key: keyof ChannelToggle;
  label: string;
  icon: JSX.Element;
  description: string;
}> = [
  {
    key: "bell",
    label: "In-app (bell)",
    icon: <Bell className="w-4 h-4 text-blue-600" />,
    description: "Appears in the top-bar bell and the My Notifications tab.",
  },
  {
    key: "push",
    label: "Push",
    icon: <Smartphone className="w-4 h-4 text-violet-600" />,
    description: "Browser / device banner — even when DocxIQ is closed.",
  },
  {
    key: "email",
    label: "Email",
    icon: <Mail className="w-4 h-4 text-emerald-600" />,
    description: "Sent to your account email.",
  },
];

// Reasonable defaults applied while waiting for the first fetch.
// Pre-fetch state shown while the GET /preferences call is in flight.
// Mirrors the backend's DEFAULT_CHANNELS: only the in-app bell is on out
// of the box; push and email require an explicit opt-in.
const DEFAULT_PREFS: NotificationPreferences = {
  enabled: true,
  channel_defaults: { bell: true, push: false, email: false },
  event_overrides: {},
};

function PreferencesTab() {
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ─── Load preferences on mount ───────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await notificationService.getPreferences();
        if (cancelled) return;
        if (data) {
          // Merge server data over defaults so any newly-introduced fields
          // remain populated even when the backend record is older.
          setPrefs({
            ...DEFAULT_PREFS,
            ...data,
            channel_defaults: { ...DEFAULT_PREFS.channel_defaults, ...(data.channel_defaults || {}) },
            event_overrides: { ...(data.event_overrides || {}) },
          });
        }
      } catch (err) {
        if (!cancelled) toast.error("Failed to load preferences");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Mutators ────────────────────────────────────────────────────────
  const setMasterEnabled = (v: boolean) => setPrefs((p) => ({ ...p, enabled: v }));

  const toggleChannel = (key: keyof ChannelToggle) =>
    setPrefs((p) => ({
      ...p,
      channel_defaults: { ...p.channel_defaults, [key]: !p.channel_defaults[key] },
    }));

  const toggleEventChannel = (eventId: string, key: keyof ChannelToggle) =>
    setPrefs((p) => {
      const current: ChannelToggle =
        p.event_overrides[eventId] ?? { ...p.channel_defaults };
      return {
        ...p,
        event_overrides: {
          ...p.event_overrides,
          [eventId]: { ...current, [key]: !current[key] },
        },
      };
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await notificationService.updatePreferences({
        enabled: prefs.enabled,
        channel_defaults: prefs.channel_defaults,
        event_overrides: prefs.event_overrides,
      });
      if (saved) {
        setPrefs((p) => ({ ...p, ...saved }));
      }
      toast.success("Preferences saved");
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.error_description || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const disabled = !prefs.enabled;

  // Resolve the current value to show for an event row — override wins over defaults.
  const eventChannelValue = (eventId: string): ChannelToggle =>
    prefs.event_overrides[eventId] ?? prefs.channel_defaults;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── 1. Master switch ───────────────────────────────────────────── */}
      <section
        className={`rounded-md p-4 border ${
          prefs.enabled ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div
              className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
                prefs.enabled ? "bg-emerald-600" : "bg-slate-400"
              }`}
            >
              <Power className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Enable notifications for DocxIQ
              </h3>
              <p className="text-xs text-slate-600 mt-0.5">
                Master switch — when OFF, DocxIQ won't send you anything across
                any channel. Critical security events still bypass this.
              </p>
            </div>
          </div>
          <Toggle value={prefs.enabled} onChange={() => setMasterEnabled(!prefs.enabled)} />
        </div>
      </section>

      {/* ── 2. Channels ───────────────────────────────────────────────── */}
      <section
        className={`rounded-md p-4 border border-slate-200 bg-white ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <h3 className="text-sm font-semibold text-slate-900 mb-1">Channels</h3>
        <p className="text-xs text-slate-600 mb-4">
          Default channels applied to every event. Per-event overrides below
          win when set.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {CHANNEL_META.map((c) => (
            <div
              key={c.key}
              className={`rounded-md border p-3 flex items-start justify-between gap-3 transition-colors ${
                prefs.channel_defaults[c.key]
                  ? "bg-blue-50 border-blue-200"
                  : "bg-slate-50 border-slate-200"
              }`}
            >
              <div className="flex items-start gap-2 min-w-0">
                <div className="flex-shrink-0 mt-0.5">{c.icon}</div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{c.label}</div>
                  <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
                    {c.description}
                  </div>
                </div>
              </div>
              <Toggle
                value={prefs.channel_defaults[c.key]}
                onChange={() => toggleChannel(c.key)}
              />
            </div>
          ))}
        </div>
      </section>

      {/* ── 3. Per-event matrix ───────────────────────────────────────── */}
      <section
        className={`rounded-md border border-slate-200 bg-white ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Event types</h3>
          <p className="text-xs text-slate-600 mt-1">
            DocxIQ currently sends notifications for{" "}
            <strong>three document events only</strong>: when a file is
            uploaded, when its AI processing finishes, and when processing
            fails. Tick the channels you want for each — the rest stay off.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Event</th>
                <th className="px-4 py-2 text-center font-semibold">Bell</th>
                <th className="px-4 py-2 text-center font-semibold">Push</th>
                <th className="px-4 py-2 text-center font-semibold">Email</th>
              </tr>
            </thead>
            <tbody>
              {EVENT_GROUPS.map((group) => (
                // Use a long-form Fragment so we can attach a stable key —
                // shorthand `<>` fragments can't carry keys, which was
                // triggering React's "each child needs a unique key" warning.
                <Fragment key={group.module}>
                  <tr className="bg-slate-50/50">
                    <td
                      colSpan={4}
                      className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600"
                    >
                      {group.module}
                    </td>
                  </tr>
                  {group.events.map((e) => {
                    const current = eventChannelValue(e.id);
                    return (
                      <tr
                        key={e.id}
                        className="border-t border-slate-100 hover:bg-slate-50/50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5">{e.icon}</div>
                            <div>
                              <div className="font-medium text-slate-900">{e.label}</div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                {e.description}
                              </div>
                              {e.required && e.required.length > 0 && (
                                <div className="text-[10px] text-amber-700 inline-flex items-center gap-1 mt-1 bg-amber-50 px-1.5 py-0.5 rounded">
                                  <AlertTriangle className="w-3 h-3" />
                                  {e.required.join(", ").toUpperCase()} required by admin
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {(["bell", "push", "email"] as const).map((ch) => {
                          const isLockedOn = e.required?.includes(ch);
                          const isUnsupported = e.unsupported?.includes(ch);
                          // Unsupported channels render forced-OFF and
                          // disabled, with a dash so the user knows the
                          // toggle isn't just stuck.
                          return (
                            <td key={ch} className="px-4 py-3 text-center align-middle">
                              {isUnsupported ? (
                                <span
                                  className="text-slate-300 text-xs"
                                  title="Not available for this event"
                                >
                                  —
                                </span>
                              ) : (
                                <Toggle
                                  size="sm"
                                  value={current[ch]}
                                  disabled={isLockedOn}
                                  onChange={() => toggleEventChannel(e.id, ch)}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 text-[11px] text-slate-500 italic border-t border-slate-100">
          That's the full list for now — only upload, processing completed and
          processing failed are wired. More event types (Reports, Workflows,
          Collaboration, Security) will land here as those modules add producers.
        </div>
      </section>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          {saving ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </div>
  );
}

/* ── Toggle switch — reusable inside this tab ───────────────────────── */
function Toggle({
  value,
  onChange,
  disabled = false,
  size = "md",
}: {
  value: boolean;
  onChange: () => void;
  disabled?: boolean;
  size?: "sm" | "md";
}) {
  const wide = size === "md" ? "w-10 h-5" : "w-8 h-4";
  const dot = size === "md" ? "w-4 h-4" : "w-3 h-3";
  const translate = size === "md" ? "translate-x-5" : "translate-x-4";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex flex-shrink-0 items-center ${wide} rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
        value ? "bg-blue-600" : "bg-slate-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block bg-white ${dot} rounded-full shadow transform transition-transform ${
          value ? translate : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export default PreferencesTab;
